import React, { useCallback, useEffect, useRef, useState } from 'react'
import { collection, doc, getDoc, getDocs, increment, orderBy, query, setDoc } from 'firebase/firestore'
import { db } from '../../firebase/firebase'
import { useTheme } from '../../contexts/ThemeContext'
import '../../assets/css/quiz.css'
import { pickDailyQuestions, todayStr } from '../../utils/quizUtils'

// ── Local storage helpers ────────────────────────────────────────────────────
const LS_KEY = 'quizHistory_v1'
const LS_STATS_KEY = 'quizStatsSent_v1'

function loadHistory() {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || [] } catch { return [] }
}

function saveDay(dateStr, score, total) {
  const hist = loadHistory().filter(h => h.d !== dateStr)
  hist.unshift({ d: dateStr, s: score, t: total })
  localStorage.setItem(LS_KEY, JSON.stringify(hist.slice(0, 30)))
}

function statsSentToday(dateStr) {
  return localStorage.getItem(LS_STATS_KEY) === dateStr
}

function markStatsSent(dateStr) {
  localStorage.setItem(LS_STATS_KEY, dateStr)
}

// ── Countdown helper ─────────────────────────────────────────────────────────
function secondsToMidnight() {
  const now = new Date()
  const midnight = new Date(now)
  midnight.setHours(24, 0, 0, 0)
  return Math.floor((midnight - now) / 1000)
}

function formatCountdown(sec) {
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

// ── Language helper ──────────────────────────────────────────────────────────
function getLang() { return localStorage.getItem('userLanguage') === 'en' ? 'en' : 'ua' }

const LABELS = ['А', 'Б', 'В', 'Г']

export const BibleQuiz = () => {
  const { isDark } = useTheme()
  const lang = getLang()
  // Frozen at mount — never changes even when midnight passes during a quiz session
  const [today] = useState(todayStr)

  const [allQuestions, setAllQuestions] = useState([])
  const [dailyQuestions, setDailyQuestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [phase, setPhase] = useState('start') // start | question | result
  const [qIndex, setQIndex] = useState(0)
  const [answers, setAnswers] = useState([])
  const [selected, setSelected] = useState(null)
  const [showFact, setShowFact] = useState(false)

  const [history, setHistory] = useState(loadHistory)
  const [countdown, setCountdown] = useState(secondsToMidnight())
  const timerRef = useRef(null)
  const [copied, setCopied] = useState(false)

  // ── Global stats ─────────────────────────────────────────────────────────
  const [globalStats, setGlobalStats] = useState(null) // { count, avg }

  // ── Load questions + today's global stats from Firestore ─────────────────
  useEffect(() => {
    Promise.all([
      getDocs(query(collection(db, 'quizQuestions'), orderBy('createdAt', 'desc'))),
      getDoc(doc(db, 'quizStats', today)),
    ]).then(([qSnap, sSnap]) => {
      const qs = qSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      setAllQuestions(qs)
      setDailyQuestions(pickDailyQuestions(qs, today))

      if (sSnap.exists()) {
        const { count, totalScore } = sSnap.data()
        if (count > 0) setGlobalStats({ count, avg: (totalScore / count).toFixed(1) })
      }

      setLoading(false)
    }).catch(err => { setError(err.message); setLoading(false) })
  }, [today])

  // ── Countdown timer ──────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'result' && !alreadyDone) return
    timerRef.current = setInterval(() => setCountdown(secondsToMidnight()), 1000)
    return () => clearInterval(timerRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase])

  const todayHistory = history.find(h => h.d === today)
  const alreadyDone = Boolean(todayHistory)
  const q = dailyQuestions[qIndex]

  const txt = useCallback((ua, en) => lang === 'en' ? (en || ua) : ua, [lang])

  // ── Save stats to Firestore (once per day per session) ───────────────────
  const saveStats = useCallback((score, total) => {
    if (statsSentToday(today)) return
    markStatsSent(today)
    const ref = doc(db, 'quizStats', today)
    setDoc(ref, {
      count: increment(1),
      totalScore: increment(score),
      perfect: increment(score === total ? 1 : 0),
    }, { merge: true }).then(() => {
      // Update local display
      setGlobalStats(prev => {
        const newCount = (prev?.count || 0) + 1
        const newTotal = (prev ? prev.avg * prev.count : 0) + score
        return { count: newCount, avg: (newTotal / newCount).toFixed(1) }
      })
    }).catch(() => {})
  }, [today])

  // ── Handlers ─────────────────────────────────────────────────────────────
  const startQuiz = () => {
    setAnswers(new Array(dailyQuestions.length).fill(null))
    setQIndex(0)
    setSelected(null)
    setShowFact(false)
    setPhase('question')
  }

  const handleAnswer = (idx) => {
    if (selected !== null) return
    setSelected(idx)
    setShowFact(true)
  }

  const next = () => {
    const newAnswers = [...answers]
    newAnswers[qIndex] = selected
    setAnswers(newAnswers)

    if (qIndex + 1 < dailyQuestions.length) {
      setQIndex(qIndex + 1)
      setSelected(null)
      setShowFact(false)
    } else {
      const finalScore = newAnswers.filter((a, i) => a === dailyQuestions[i]?.c).length
      saveDay(today, finalScore, dailyQuestions.length)
      saveStats(finalScore, dailyQuestions.length)
      setHistory(loadHistory())
      setCountdown(secondsToMidnight())
      setPhase('result')
    }
  }

  const handleShare = (score, total) => {
    const line = lang === 'en'
      ? `📖 Bible Quiz · ${today}\n${score}/${total} correct!\nTry it: https://ixtus.org.ua/church/#/bible-quiz`
      : `📖 Біблійний Квіз · ${today}\n${score}/${total} правильних!\nСпробуй: https://ixtus.org.ua/church/#/bible-quiz`
    navigator.clipboard.writeText(line).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500) })
  }

  // ── Global stats widget ──────────────────────────────────────────────────
  const renderGlobalStats = () => {
    if (!globalStats || globalStats.count < 1) return null
    const total = dailyQuestions.length || 10
    return (
      <div className='qz-global-stats'>
        <span>📊 {txt('Сьогодні пройшли', 'Completed today')}:&nbsp;<strong>{globalStats.count}</strong></span>
        <span className='qz-stats-sep'>·</span>
        <span>{txt('Середній результат', 'Avg score')}:&nbsp;<strong>{globalStats.avg}/{total}</strong></span>
      </div>
    )
  }

  // ── History widget ───────────────────────────────────────────────────────
  const renderHistory = () => {
    const last7 = history.slice(0, 7)
    if (last7.length === 0) return null
    return (
      <div className='qz-history'>
        <div className='qz-history-title'>{txt('Ваш прогрес', 'Your progress')}</div>
        <div className='qz-history-days'>
          {last7.map(h => {
            const pct = h.t ? h.s / h.t : 0
            const cls = pct >= 0.8 ? 'dot-great' : pct >= 0.5 ? 'dot-ok' : 'dot-low'
            return (
              <div key={h.d} className='qz-history-day'>
                <div className={`qz-dot ${cls}`}>{h.s}/{h.t}</div>
                <div className='qz-dot-date'>{h.d.slice(5)}</div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // ── START screen ─────────────────────────────────────────────────────────
  if (phase === 'start') {
    return (
      <div className={`qz-page ${isDark ? 'qz-dark' : ''}`}>
        <div className='qz-card qz-start'>
          <div className='qz-badge'>📖</div>
          <h1 className='qz-title'>{txt('Біблійний Квіз', 'Bible Quiz')}</h1>
          <p className='qz-subtitle'>
            {txt('Щодня нові 10 запитань. Перевір свої знання Святого Письма!',
                 'New 10 questions every day. Test your knowledge of Scripture!')}
          </p>

          {loading && <div className='qz-loading'>{txt('Завантаження…', 'Loading…')}</div>}
          {error && <div className='qz-error'>{txt('Помилка: ', 'Error: ')}{error}</div>}

          {!loading && !error && allQuestions.length === 0 && (
            <div className='qz-error'>
              {txt('Питань ще немає. База поповнюється.', 'No questions yet. Database coming soon.')}
            </div>
          )}

          {renderGlobalStats()}
          {renderHistory()}

          {!loading && !error && allQuestions.length > 0 && (
            alreadyDone
              ? <div className='qz-done-box'>
                  <div className='qz-done-score'>
                    {txt('Сьогодні:', "Today:")}&nbsp;
                    <strong>{todayHistory.s}/{todayHistory.t}</strong>
                  </div>
                  <div className='qz-done-next'>
                    {txt('Наступний квіз через', 'Next quiz in')}
                    <span className='qz-countdown'>{formatCountdown(countdown)}</span>
                  </div>
                </div>
              : <button className='qz-btn-start' onClick={startQuiz}>
                  {txt('Почати', 'Start Quiz')} →
                </button>
          )}

        </div>
      </div>
    )
  }

  // ── QUESTION screen ──────────────────────────────────────────────────────
  if (phase === 'question' && q) {
    const options = lang === 'en' && q.oe?.length ? q.oe : q.ou
    const questionText = lang === 'en' && q.en ? q.en : q.ua
    const factText = lang === 'en' && q.fe ? q.fe : q.fu
    const isCorrect = selected === q.c
    const doneCount = answers.filter((a, i) => i < qIndex && a === dailyQuestions[i]?.c).length

    return (
      <div className={`qz-page ${isDark ? 'qz-dark' : ''}`}>
        <div className='qz-card'>
          <div className='qz-progress-wrap'>
            <div className='qz-progress-bar' style={{ width: `${(qIndex / dailyQuestions.length) * 100}%` }} />
          </div>
          <div className='qz-progress-label'>
            <span>{txt(`Питання ${qIndex + 1} з ${dailyQuestions.length}`, `Question ${qIndex + 1} of ${dailyQuestions.length}`)}</span>
            <span className='qz-score-pill'>{doneCount}/{qIndex} ✓</span>
          </div>

          <div className='qz-question'>{questionText}</div>

          <div className='qz-options'>
            {options?.map((opt, i) => {
              let cls = 'qz-opt'
              if (selected !== null) {
                if (i === q.c) cls += ' qz-opt--correct'
                else if (i === selected) cls += ' qz-opt--wrong'
              }
              return (
                <button key={i} className={cls} onClick={() => handleAnswer(i)} disabled={selected !== null}>
                  <span className='qz-opt-letter'>{LABELS[i]}</span>
                  <span className='qz-opt-text'>{opt}</span>
                </button>
              )
            })}
          </div>

          {showFact && (
            <div className={`qz-fact ${isCorrect ? 'qz-fact--correct' : 'qz-fact--wrong'}`}>
              <div className='qz-fact-head'>
                {isCorrect ? '✅ ' + txt('Правильно!', 'Correct!') : '❌ ' + txt('Неправильно', 'Incorrect')}
              </div>
              {factText && <div className='qz-fact-text'>{factText}</div>}
              <button className='qz-btn-next' onClick={next}>
                {qIndex + 1 < dailyQuestions.length
                  ? txt('Далі →', 'Next →')
                  : txt('Переглянути результат →', 'See result →')}
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── RESULT screen ────────────────────────────────────────────────────────
  const finalScore = answers.filter((a, i) => a === dailyQuestions[i]?.c).length
  const pct = dailyQuestions.length ? finalScore / dailyQuestions.length : 0
  const medal = pct === 1 ? '🏆' : pct >= 0.8 ? '🥇' : pct >= 0.6 ? '🥈' : pct >= 0.4 ? '🥉' : '📖'

  return (
    <div className={`qz-page ${isDark ? 'qz-dark' : ''}`}>
      <div className='qz-card qz-result'>
        <div className='qz-badge'>{medal}</div>
        <h2 className='qz-result-title'>{txt('Результат', 'Result')}</h2>
        <div className='qz-result-score'>{finalScore}/{dailyQuestions.length}</div>
        <div className='qz-result-sub'>
          {pct === 1 && txt('Ідеально! Ви знаєте Біблію чудово!', 'Perfect! You know the Bible wonderfully!')}
          {pct >= 0.8 && pct < 1 && txt('Чудовий результат!', 'Excellent result!')}
          {pct >= 0.6 && pct < 0.8 && txt('Хороший результат. Продовжуйте читати Біблію!', 'Good result. Keep reading the Bible!')}
          {pct >= 0.4 && pct < 0.6 && txt('Непогано! Є куди зростати.', 'Not bad! Room to grow.')}
          {pct < 0.4 && txt('Не засмучуйтесь — завтра новий шанс!', "Don't worry — new chance tomorrow!")}
        </div>

        {renderGlobalStats()}

        <div className='qz-review'>
          {dailyQuestions.map((dq, i) => {
            const userAns = answers[i]
            const correct = userAns === dq.c
            const optArr = lang === 'en' && dq.oe?.length ? dq.oe : dq.ou
            return (
              <div key={dq.id || i} className={`qz-review-row ${correct ? 'qz-review-row--ok' : 'qz-review-row--bad'}`}>
                <span className='qz-review-icon'>{correct ? '✅' : '❌'}</span>
                <div>
                  <div className='qz-review-q'>{lang === 'en' && dq.en ? dq.en : dq.ua}</div>
                  {!correct && userAns !== null && (
                    <div className='qz-review-ans'>
                      <span className='qz-review-wrong'>{LABELS[userAns]}: {optArr?.[userAns]}</span>
                      <span className='qz-review-correct'> → {LABELS[dq.c]}: {optArr?.[dq.c]}</span>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <div className='qz-next-box'>
          <div className='qz-next-label'>{txt('Наступний квіз через', 'Next quiz in')}</div>
          <div className='qz-countdown'>{formatCountdown(countdown)}</div>
        </div>

        <button className='qz-btn-share' onClick={() => handleShare(finalScore, dailyQuestions.length)}>
          {copied ? txt('Скопійовано!', 'Copied!') : txt('📤 Поділитися результатом', '📤 Share result')}
        </button>

        {renderHistory()}
      </div>
    </div>
  )
}

export default BibleQuiz
